import React from 'react';
import cn from 'classnames';
import IconDraggable from '@creditflowresearch/uilib/Icon/IconDraggable';
import { ICellProps } from '../../interfaces';
import styles from '../../dataTable.module.scss';

export interface IBodyCellProps {
  className?: string,
  name: string,
  title?: React.ReactNode,
  timeZone?: string,
  defaultContent?: string,
  rowIndex: number,
  rowData: object,
  headCallback?: () => void,
  tooltipContent?: string,
  cellCallback?: (data: ICellProps) => string | number | React.ReactNode,
  isVisible?: boolean,
  isDraggable?: boolean,
  isExpanded?: boolean,
  sortable?: boolean,
  stickyLeft?: boolean,
  width?: number | string,
  maxWidth?: number | string,
  minWidth?: number | string,
  listeners?: object,
  attributes?: object,
  hiddenRows?: number[],
  isHovered?: boolean,
  customCellProps?: { [key: string]: any },
  stickyRightPosition?: number,
  colSpan?: number,
  dataTest?: string,
  noWrap?: boolean,
}

const BodyCell = ({
  className = '',
  defaultContent = '',
  headCallback,
  cellCallback = null,
  isVisible = true,
  isDraggable = false,
  isExpanded = false,
  stickyLeft,
  name,
  title,
  rowIndex,
  rowData,
  sortable,
  tooltipContent,
  width,
  minWidth,
  maxWidth,
  listeners,
  attributes,
  hiddenRows = [],
  isHovered,
  customCellProps,
  stickyRightPosition,
  dataTest,
  noWrap = false,
  ...otherProps
}: IBodyCellProps) => {
  if (!isVisible || hiddenRows.includes(Number(rowIndex))) {
    return null;
  }

  const cellProps = Object.keys(otherProps).reduce((cellProps, propName) => ({
    ...cellProps,
    [propName]: typeof otherProps[propName] === 'function'
      ? (event) => {
        otherProps[propName]({
          event,
          cellData: rowData[name],
          cellName: name,
          cellTitle: title,
          row: rowData,
          rowIndex,
        });
      } : otherProps[propName].toString(),
  }), {});

  const renderContent = () => {
    if (isDraggable) {
      return (
        <span { ...listeners } { ...attributes }>
          <IconDraggable />
        </span>
      );
    }

    if (typeof cellCallback === 'function') {
      return cellCallback({
        cellData: rowData[name],
        cellName: name,
        cellTitle: title,
        row: rowData,
        isExpanded,
        rowIndex,
        isHovered,
        ...customCellProps ? { customCellProps } : {},
      });
    }

    if (typeof rowData[name] === 'string' || typeof rowData[name] === 'number') {
      return rowData[name];
    }

    return defaultContent;
  };

  return (
    <td
      style={ {
        ...(width && { width: typeof width === 'number' ? `${ width }px` : width }),
        ...(minWidth && { minWidth: typeof minWidth === 'number' ? `${ minWidth }px` : minWidth }),
        ...(maxWidth && { maxWidth: typeof maxWidth === 'number' ? `${ maxWidth }px` : maxWidth }),
        ...(stickyRightPosition && { right: `${ stickyRightPosition }px` }),
      } }
      className={ cn(className, { [styles.noWrap]: noWrap }) }
      data-test={ dataTest || name }
      { ...cellProps }
    >
      { renderContent() }
    </td>
  );
};

export default BodyCell;
